<template>
  <VueFinalModal
    v-model="isOpen"
    content-transition="vfm-fade"
    :click-to-close="true"
    class="flex justify-center items-center p-6"
    content-class="relative w-full max-w-5xl h-[85vh] bg-[#fafbfc] rounded-[32px] shadow-2xl flex overflow-hidden z-50 ring-1 ring-black/5"
    @close="close"
  >
    <div class="flex w-full h-full min-h-0">
      <aside class="w-72 bg-[#1c4848] text-white p-8 flex flex-col justify-between relative overflow-hidden shrink-0">
        <div class="absolute inset-0 opacity-10 mix-blend-overlay pointer-events-none" style="background-image: url('https://grainy-gradients.vercel.app/noise.svg')" />
        <div class="relative z-10 flex flex-col h-full">
          <div class="flex items-center gap-3 mb-6 opacity-80">
            <div class="w-8 h-8 rounded-full bg-white/10 border border-white/10 flex items-center justify-center text-[#ecd79f]">
              <IconSlidingPuzzle class="w-[18px] h-[18px]" />
            </div>
            <span class="text-xs font-bold tracking-[0.2em] uppercase">Playing</span>
          </div>
          <h3 class="font-serif text-2xl font-medium tracking-tight mb-1 text-[#f2f4f4]">
            {{ (gameLoaded || game)?.name || 'Puzzle' }}
          </h3>
          <p class="text-xs text-white/50 font-medium uppercase tracking-wider mb-8">Sliding Puzzle</p>
          <div class="flex-1 space-y-6">
            <div class="bg-white/5 p-3 rounded-2xl border border-white/5">
              <p class="text-[10px] font-bold text-[#ecd79f] uppercase tracking-wider mb-3 flex items-center gap-1">
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                Goal
              </p>
              <div class="aspect-square rounded-xl overflow-hidden border border-white/10 shadow-sm bg-[#2d3748]">
                <img v-if="puzzleImageUrl" :src="puzzleImageUrl" class="w-full h-full object-cover" alt="">
              </div>
            </div>
            <div class="grid grid-cols-2 gap-3">
              <div class="bg-white/5 rounded-xl p-3 border border-white/5">
                <p class="text-[9px] font-bold text-white/40 uppercase tracking-wider mb-1">Time</p>
                <p class="text-xl font-bold font-mono">{{ timerLabel }}</p>
              </div>
              <div class="bg-white/5 rounded-xl p-3 border border-white/5">
                <p class="text-[9px] font-bold text-white/40 uppercase tracking-wider mb-1">Moves</p>
                <p class="text-xl font-bold font-mono">{{ gameStats.moves }}</p>
              </div>
            </div>
          </div>
          <div class="mt-auto space-y-3 pt-6">
            <button type="button" class="w-full py-3.5 rounded-xl bg-[#ecd79f] text-[#1c4848] font-bold text-sm hover:bg-[#ffe082] hover:shadow-lg hover:shadow-[#ecd79f]/20 transition-all flex items-center justify-center gap-2" @click="onRestart">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/><path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16"/><path d="M21 21v-5h-5"/></svg>
              Shuffle Tiles
            </button>
            <button type="button" class="w-full py-3.5 rounded-xl bg-white/5 text-white/60 font-bold text-sm hover:bg-white/10 hover:text-white transition-colors" @click="close">Exit to Library</button>
          </div>
        </div>
      </aside>
      <main class="flex-1 bg-[#f2f4f4] relative p-8 flex items-center justify-center overflow-y-auto min-h-0">
        <div v-if="!ready" class="text-[#1c4848]/50 text-sm">Loading game…</div>
        <SlidingPuzzlePlay v-else-if="gameLoaded" ref="puzzlePlayRef" :game="gameLoaded" @win="onWin" @stats="gameStats = $event" />
      </main>
    </div>
  </VueFinalModal>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { VueFinalModal } from 'vue-final-modal';
import SlidingPuzzlePlay from '@/components/app/dashboard/games/play/SlidingPuzzlePlay.vue';
import IconSlidingPuzzle from '@/components/app/dashboard/games/icons/IconSlidingPuzzle.vue';
import { useGamesStore } from '@/store/games/index.js';

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  game: { type: Object, default: null },
});

const emit = defineEmits(['update:modelValue', 'close']);

const isOpen = computed({
  get: () => props.modelValue,
  set: (v) => emit('update:modelValue', v),
});

const gamesStore = useGamesStore();
const gameLoaded = ref(null);
const gameStats = ref({ moves: 0, time: 0 });
const puzzlePlayRef = ref(null);

const puzzleImageUrl = computed(() => {
  const g = gameLoaded.value || props.game;
  const a = g?.assets || [];
  return Array.isArray(a) && a[0]?.url ? a[0].url : '';
});

const timerLabel = computed(() => {
  const s = gameStats.value.time || 0;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
});

const ready = computed(() => {
  const g = gameLoaded.value || props.game;
  if (!g?.id) return false;
  const assets = g.assets || [];
  return Array.isArray(assets) && assets.length > 0 && assets[0]?.url;
});

watch(() => [props.modelValue, props.game?.id], async () => {
  if (!props.modelValue || !props.game?.id) {
    gameLoaded.value = null;
    gameStats.value = { moves: 0, time: 0 };
    return;
  }
  const hasAsset = (props.game.assets || [])[0]?.url;
  if (hasAsset) {
    gameLoaded.value = props.game;
    return;
  }
  try {
    const loaded = await gamesStore.fetchOne(props.game.id);
    gameLoaded.value = loaded;
  } catch {
    gameLoaded.value = props.game;
  }
}, { immediate: true });

function close() {
  emit('update:modelValue', false);
  emit('close');
}

function onRestart() {
  puzzlePlayRef.value?.restart?.();
}

function onWin() {}
</script>
